/**
 * PreflightChecklist — exibe os 9 checks do POST /admin/ztna/onboard/preflight.
 *
 * Cada check tem badge OK/WARN/FAIL. Rodape resume contagens.
 * Block submit eh feito via prop `canSubmit` no caller (Step4Review),
 * que vem de PreflightResponse.can_submit.
 */
import { Badge, Group, Skeleton, Stack, Text, ThemeIcon } from '@mantine/core'
import { IconAlertTriangle, IconCheck, IconX } from '@tabler/icons-react'
import type { PreflightCheckResult, PreflightResponse } from '../types'

interface Props {
  result: PreflightResponse | null | undefined
  isLoading?: boolean
  error?: Error | null
}

function statusBadge(status: PreflightCheckResult['status']) {
  if (status === 'ok') {
    return (
      <ThemeIcon size="sm" radius="xl" color="green" variant="light">
        <IconCheck size={12} />
      </ThemeIcon>
    )
  }
  if (status === 'warn') {
    return (
      <ThemeIcon size="sm" radius="xl" color="yellow" variant="light">
        <IconAlertTriangle size={12} />
      </ThemeIcon>
    )
  }
  return (
    <ThemeIcon size="sm" radius="xl" color="red" variant="light">
      <IconX size={12} />
    </ThemeIcon>
  )
}

export function PreflightChecklist({ result, isLoading, error }: Props) {
  if (isLoading) {
    return (
      <Stack gap={6}>
        <Skeleton height={20} />
        <Skeleton height={20} />
        <Skeleton height={20} />
        <Skeleton height={20} />
      </Stack>
    )
  }

  if (error) {
    return (
      <Group gap={6} c="red">
        <IconX size={14} />
        <Text size="xs">Pre-flight falhou: {error.message}</Text>
      </Group>
    )
  }

  if (!result || !result.checks || result.checks.length === 0) {
    return (
      <Text size="xs" c="dimmed">
        Pre-flight ainda não executado. Aguardando dados completos.
      </Text>
    )
  }

  const okCount = result.checks.filter((c) => c.status === 'ok').length
  const warnCount = result.checks.filter((c) => c.status === 'warn').length
  const failCount = result.checks.filter((c) => c.status === 'fail').length

  return (
    <Stack gap={8}>
      {result.checks.map((check) => (
        <Group key={check.name} gap={8} wrap="nowrap" align="flex-start">
          {statusBadge(check.status)}
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text size="sm" fw={500}>{check.name}</Text>
            <Text size="xs" c="dimmed">{check.detail}</Text>
          </div>
        </Group>
      ))}
      <Group gap={6} mt={6} pt={6} style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
        <Badge color="green" variant="light">{okCount} OK</Badge>
        {warnCount > 0 ? <Badge color="yellow" variant="light">{warnCount} warning</Badge> : null}
        {failCount > 0 ? <Badge color="red" variant="light">{failCount} blockers</Badge> : null}
        <Text size="xs" c="dimmed">
          {result.can_submit ? '— pronto pra provisionar.' : '— corrija blockers pra prosseguir.'}
        </Text>
      </Group>
    </Stack>
  )
}
